import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { first } from 'lodash';

import beRefundPayment from 'actions/payment/beRefundPayment.action';
import { allFieldsAmount, allFieldsUsedAmount } from 'bl/fields.bl';
import { ModalContext } from 'components/core/ModalRoot.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import CurrencySign from 'components/price/CurrencySign.component';
import PAYMENT_TYPE from 'constants/paymentType.const';
import STAFF_GROUP from 'constants/staff/group.const';
import S from 'constants/string.const';
import { useCheckoutPermissions } from 'hooks/useCheckoutPermissions.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCashupSelectedStaffGroup } from 'selectors/cashup.selectors';
import { selectCurrentSalonClientsArray } from 'selectors/currentSalon.selectors';
import asArray from 'utils/arrays/asArray.util';
import sumArray from 'utils/arrays/sumArray.util';
import fromStatusToMessageV1 from 'utils/converters/backend/fromStatusToMessageV1.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import withDefaultPrevented from 'utils/dom/withDefaultPrevented';
import call from 'utils/functions/call.util';
import withFeedback from 'utils/functions/withFeedback.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseDate from 'utils/parsers/parseDate';
import byId from 'utils/predicates/byId.util';
import fixed from 'utils/values/fixed.util';
import parsfixed from 'utils/values/parsfixed.util';

import CashupItemRefundWizard from 'cashup/CashupItemRefundWizard.component';
import CashupLogItemErrorModal from 'cashup/CashupLogItemErrorModal.component';
import CashupLogItemModal from 'cashup/CashupLogItemModal.component';
import RefundButton from 'cashup/RefundButton.component';

import CN from './CashupLogItem.module.scss';

const CTAG = 'CashupLogItem';
const L = logger(CTAG);

const TooltipRefund = ({ t }) => <div data-bem="CashupLogItem--TooltipRefund">{t('cashUp.cashupLogs.tooltip.refund')}</div>;

const determineClientDisplayName = ({ clients, clientId }) => {
  const filteredClients = asArray(clients).filter(byId(clientId));
  const client = asObject(first(filteredClients));
  return fromAnyToString(client.user_name) || S.mdash;
};

const calculateTotalDeposits = (deposits) => {
  if (!deposits) {
    return 0;
  }

  const { clientDeposits } = deposits;
  const amounts = clientDeposits ? allFieldsAmount(clientDeposits) : allFieldsAmount(deposits).map(Math.abs);

  return sumArray(amounts);
};

const determineNoShowText = ({ isNoShowFee, depositSum, onlineAmount, t }) => {
  if (isNoShowFee && onlineAmount > 0) {
    return t('cashUp.cashupLogs.logItems.noshowFee');
  }

  if (isNoShowFee && depositSum > 0) {
    return t('cashUp.cashupLogs.logItems.depositKeptFee');
  }

  return '';
};

const getTotalClientBill = ({ total, voucher, vouchersUsed, deposits, deposit }) => {
  // vouchers
  const voucherUsedAmount = vouchersUsed?.length ? sumArray(allFieldsUsedAmount(vouchersUsed)) : voucher;

  // deposits
  const depositUsedAmount = deposits ? calculateTotalDeposits(deposits) : deposit;

  // final total for All Staff
  return Math.abs(fromAnyToNumber(total) || 0) + Math.abs(fromAnyToNumber(voucherUsedAmount) || 0) + Math.abs(fromAnyToNumber(depositUsedAmount) || 0);
};

const CashupLogItemBase = ({ data, onViewReceipt, onVoidTransaction, onCheckout, isDarkMode, reCheckoutVisible: isReCheckoutVisible }) => {
  const [isMainShown, setIsMainShown] = useState(false);
  const [isRefundWizardShown, setIsRefundWizardShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { formatDate } = useGilt();
  const { t } = useTranslation();
  const clients = useSelector(selectCurrentSalonClientsArray);
  const selectedStaffGroup = useSelector(selectCashupSelectedStaffGroup);
  const dispatch = useDispatch();
  const { canVoidTransaction } = useCheckoutPermissions();

  const handleViewReceipt = withDefaultPrevented(() => {
    call(`${CTAG}.onViewReceipt`, onViewReceipt, data);
  });

  const refundWithIntent = useCallback(async () => {
    const { intent_id: paymentIntentId, cardAmount: refundAmount } = asObject(data);

    if (!paymentIntentId) {
      L.debug('refundWithIntent()', 'paymentIntentId:', paymentIntentId);
      return null;
    }

    const result = await dispatch(beRefundPayment({ paymentIntentId, refundAmount }));
    const { status, is } = asObject(result);
    const message = is.ok ? '' : fromStatusToMessageV1(status);

    L.debug('refundWithIntent()', 'message:', message, 'result:', result);
    return message;
  }, [dispatch, data]);

  const voidTransaction = useCallback(
    async ({ showModal }) => {
      const message = await refundWithIntent();
      L.debug('voidTransaction()', 'message:', message);

      if (message) {
        showModal(CashupLogItemErrorModal, { message });
      } else {
        setIsMainShown(false);
        call(`${CTAG}.onVoidTransaction`, onVoidTransaction, data);
      }
    },
    [data, onVoidTransaction, refundWithIntent],
  );

  const handleCheckout = () => {
    const { created_from: txId, clients: txClients } = asObject(data);

    const firstClient = first(txClients);

    if (!firstClient) {
      L.warn('handleCheckout()', 'There is no firstClient:', firstClient);
    }

    const { appointmentId, clientId } = asObject(firstClient);

    if (txId) {
      onCheckout(txId, clientId, appointmentId || 0);
    }
  };

  const handleVoidTx = withFeedback(
    (isLoading) => {
      L.debug('handleVoidTx()', 'isLoading:', isLoading);
      setIsLoading(isLoading);
    },
    (...args) => voidTransaction(...args),
  );

  // TODO: extract the function under ModalContext.Consumer as a separate component
  return (
    <ModalContext.Consumer>
      {({ showModal }) => {
        const {
          id,
          voucher,
          partCash,
          otherAmount,
          onlineAmount,
          deposit,
          total,
          deposits,
          created,
          cardAmount,
          paymentType,
          tipAmount,
          is_void: isVoid,
          clients: clientsFromData,
          void_created: voidCreated,
          vouchers_used: vouchersUsed,
          is_no_show_fee: isNoShowFee,
        } = asObject(data);

        const clientDescriptions = asArray(clientsFromData)
          .map((client) =>
            !(client && client.id)
              ? t('common.walkIn')
              : determineClientDisplayName({
                  clients,
                  clientId: client.id,
                }),
          )
          .join(', ');

        const cypressId = id && `transaction-id-${id}`;

        const depositSum = calculateTotalDeposits(deposits);
        const voucherAmount = vouchersUsed ? sumArray(allFieldsUsedAmount(vouchersUsed)) : 0;
        const totalSum = getTotalClientBill({
          total,
          voucher,
          vouchersUsed,
          deposits,
          deposit,
        });

        const noShowText = determineNoShowText({ isNoShowFee, depositSum, onlineAmount, t });
        const isSlickPos = paymentType === PAYMENT_TYPE.slickPos;

        const createdAsDate = parseDate(created);
        const displayTime = formatDate(createdAsDate, { hideDate: true });
        const displayDate = formatDate(createdAsDate, { hideTime: true, hideWeekday: true });

        const displayOnlineAmount = onlineAmount ? fixed(Math.abs(onlineAmount)) : 0;
        const displayCardAmount = cardAmount ? fixed(Math.abs(cardAmount)) : 0;
        const displayOtherAmount = otherAmount ? fixed(Math.abs(otherAmount)) : 0;
        const displayDepositSum = depositSum ? fixed(Math.abs(depositSum)) : 0;
        const displayVoucherAmount = voucherAmount ? fixed(Math.abs(voucherAmount)) : voucherAmount;
        const displayDeposit = deposit ? parsfixed(deposit) : 0;
        const displayVoucher = voucher ? fixed(voucher) : 0;
        const displayTipAmount = tipAmount ? fixed(tipAmount) : 0;
        const displayPartCash = partCash ? fixed(partCash) : 0;
        const displayTotalByStylist = fromAnyToNumber(deposit) + fromAnyToNumber(total);

        const isNoShowTextRendered = fromAnyToBool(isNoShowFee);
        const isViewReceiptRendered = fromAnyToBool(!isNoShowFee && !isVoid);
        const isRecheckoutRendered = fromAnyToBool(isVoid && isReCheckoutVisible);
        const isWizRendered = fromAnyToBool(isRefundWizardShown);
        const isRefundButtonRendered = fromAnyToBool(
          // SEE: https://getslick.atlassian.net/browse/GS-11596
          !(selectedStaffGroup && selectedStaffGroup !== STAFF_GROUP.all && selectedStaffGroup !== STAFF_GROUP.salon) && !isNoShowFee && !(onlineAmount > 0) && !isVoid && !isMainShown,
        );
        const isRefundTextRendered = fromAnyToBool(isVoid);
        const isRefundDisabled = fromAnyToBool(isMainShown || isLoading);

        const displayRefundedAmount = Math.abs(total);

        const handleWizardChange = ({ refund, close }) => {
          if (refund) {
            L.unignore('handleWizardChange()', 'handleVoidTx:', handleVoidTx({ showModal }));
          }

          if (close) {
            setIsMainShown(false);
            setIsRefundWizardShown(false);
          }
        };

        const handleRefund = withDefaultPrevented(() => {
          if (voidCreated || isRefundDisabled) {
            return;
          }

          if (isSlickPos) {
            setIsMainShown(true);
            setIsRefundWizardShown(true);
          } else {
            setIsMainShown(true);

            const onClose = () => void setIsMainShown(false);
            const onConfirm = () => void handleVoidTx({ showModal });

            showModal(CashupLogItemModal, { total: totalSum, onClose, onConfirm });
          }
        });
        L.debug('CashupLogItem () total client bill incl tips', {
          total,
          deposit,
          depositSum,
          voucher,
          vouchersUsed,
          voucherAmount,
          totalSum,
          displayTotalByStylist,
          selectedStaffGroup,
        });

        return (
          <div
            data-bem="CashupLogItem"
            data-cy={cypressId}
            className={cn({
              [CN.dark]: isDarkMode,
              [CN.tableRow]: true,
              [CN.redUnderline]: isVoid,
            })}
          >
            <div className={CN.fixedColumnCell}>
              <span>{displayTime} </span>
              <span>{displayDate}</span>
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.fixedSecondCell]: true,
              })}
            >
              {clientDescriptions}
            </div>
            <div className={CN.cell}>
              <CurrencySign />
              {displayPartCash}
            </div>
            <div className={CN.cell}>
              <CurrencySign />
              {displayOnlineAmount}
            </div>
            <div className={CN.cell}>
              <CurrencySign />
              {displayCardAmount}
            </div>
            <div className={CN.cell}>
              <CurrencySign />
              {displayOtherAmount}
            </div>

            <div className={CN.cell}>
              {depositSum ? (
                <>
                  <CurrencySign />
                  {displayDepositSum}
                </>
              ) : (
                <>
                  <CurrencySign />
                  {displayDeposit}
                </>
              )}
            </div>

            <div className={CN.cell}>
              {voucherAmount ? (
                <>
                  <CurrencySign />
                  {displayVoucherAmount}
                </>
              ) : (
                <>
                  <CurrencySign />
                  {displayVoucher}
                </>
              )}
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.textBold]: true,
              })}
            >
              <CurrencySign value={totalSum || 0} />
            </div>

            <div data-bem="CashupLogItem__deposit" className={CN.cell}>
              <CurrencySign />
              {displayTipAmount}
            </div>

            <div data-bem="CashupLogItem__actions" className={CN.cell}>
              <div className={CN.buttonContainer}>
                {isNoShowTextRendered && (
                  <div data-bem="CashupLogItem__actions-text" className={CN.noShowText}>
                    {noShowText}
                  </div>
                )}

                {isViewReceiptRendered && (selectedStaffGroup === STAFF_GROUP.all || selectedStaffGroup === STAFF_GROUP.salon) && (
                  <div data-bem="CashupLogItem__actions-view" className="td-view-receipt">
                    <Button variant="secondary" onClick={handleViewReceipt} tag={`CashupLogItem__actions-view-button--${id}`}>
                      <div className={CN.wrapText}>{t('cashUp.cashupLogs.buttons.viewReceipt')}</div>
                    </Button>
                  </div>
                )}
                {isRefundTextRendered && (
                  <div
                    data-bem="CashupLogItem__actions-refunded"
                    className={cn({
                      [CN.center]: true,
                      'text-voided': true,
                    })}
                  >
                    {displayDepositSum === 0 ? (
                      <div className={CN.bold}>
                        <CurrencySign />
                        {displayRefundedAmount}
                        <span className={CN.textButton}>{t('cashUp.cashupLogs.buttons.refunded')}</span>
                      </div>
                    ) : (
                      <Tooltip
                        variant="top"
                        content={
                          <div className={CN.tooltipText}>
                            <CurrencySign />
                            {t('cashUp.cashupLogs.tooltip.addedBackDepodsit', { deposit: displayDepositSum })}
                          </div>
                        }
                      >
                        <div className={CN.bold}>
                          <CurrencySign />
                          {displayRefundedAmount}
                        </div>
                        <div>
                          <span className={CN.textButton}>{t('cashUp.cashupLogs.buttons.refunded')}</span>
                          <Icon variant="info" width="lg" />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>

            {fromAnyToBool(id) && (
              <div
                data-bem="CashupLogItem__actions"
                className={cn({
                  [CN.cell]: true,
                  [CN.exclude]: true,
                })}
              >
                <div className={CN.buttonContainer}>
                  {(isNoShowTextRendered || onlineAmount > 0) && (
                    <Tooltip variant="leftToBottom" width="max" content={<TooltipRefund t={t} />}>
                      <div className={CN.refundTooltipContainer}>
                        <Icon variant="info" width="sm" />
                        <Button variant="primary" disabled>
                          <div className={CN.wrapText}>{t('cashUp.cashupLogs.buttons.refundEdit')}</div>
                        </Button>
                      </div>
                    </Tooltip>
                  )}
                  {isRefundButtonRendered && canVoidTransaction && (
                    <div data-bem="CashupLogItem__actions-refund">
                      <RefundButton onClick={handleRefund} disabled={isRefundDisabled} isLoading={isLoading} />
                    </div>
                  )}

                  {isRecheckoutRendered && (
                    <Button variant="emphasis" onClick={handleCheckout}>
                      <div className={CN.wrapText}>{t('cashUp.cashupLogs.buttons.recheckout')}</div>
                    </Button>
                  )}

                  {isWizRendered && <CashupItemRefundWizard client={clientDescriptions} total={totalSum} onChange={handleWizardChange} />}
                </div>
              </div>
            )}
          </div>
        );
      }}
    </ModalContext.Consumer>
  );
};

// this extra export is needed for tests that provide their own mock for connect()
export { CashupLogItemBase as CashupLogItem4Test };

export const CashupLogItem = withTheme()(CashupLogItemBase);
